import * as I from "immutable";
import { combineEpics } from "redux-observable";
import { merge as mergeStatic, of } from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    ignoreElements,
    map,
    merge,
    mergeMap,
    tap,
} from "rxjs/operators";

import { Action, actionCreators, isOfAction, isOfActions } from "../actions";
import { getFlag, getPeristent, getToken, isValidKey } from "../reducers/persist";
import {
    getFlattenRoomViews,
    MAX_OPEN_ROOMS_NUM,
    RoomType,
    RoomView,
    unflattenRoomViews,
} from "../reducers/rooms";
import { PersistObject } from "../service/Utils";
import { notUndefined } from "../utils/any";
import { listTakeLast } from "../utils/immutable";
import { uuid } from "../utils/short-uuid";
import { Epic } from "../utils/types";

import { desktopAPI } from "./electronIntegration";

// helpers

const storeString = (key: string, value: string | undefined) => {
    try {
        if (value !== undefined) {
            localStorage.setItem(key, value);
        } else {
            localStorage.removeItem(key);
        }
    } catch (e) {
        console.error("failed to save to localStorage", key, value);
    }
};

const changesActiveRooms = (x: Action) =>
    x.type === actionCreators.setRoomViewAsActive.type || changesOpenedRooms(x);

const changesOpenedRooms = (x: Action) =>
    x.type === actionCreators.onLogout.type ||
    x.type === actionCreators.openRoomSucceeded.type ||
    x.type === actionCreators.closeRoomSucceeded.type ||
    x.type === actionCreators.roomSearchQueryUpdated.type;

// epics

const onShowAppDevTools: Epic = (action$, state$, { App }) =>
    state$.pipe(
        map(x => getFlag(x, "show_app_dev_tools")),
        distinctUntilChanged(),
        tap(value => {
            if (value) {
                desktopAPI.openDevTools();
            }
        }),
        ignoreElements()
    );

const onStoreObject: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.storeObject)),
        tap(({ payload }) => {
            if (payload.customEnvironment) {
                const token = getToken(state$.value);
                if (token) {
                    App.protoProxy.disconnect();
                }
                App.setupProto();
                if (token) {
                    App.actionCreators.setProtoToken({ token });
                }
            }
        }),
        map(({ payload }) => Object.keys(payload).filter(isValidKey)),
        merge(
            action$.pipe(
                filter(isOfAction(actionCreators.toggleFlag)),
                map(() => ["debug_flags" as const])
            )
        ),
        tap(keys => {
            const persist = getPeristent(state$.value);
            for (const key of keys) {
                if (key === "token") {
                    const value = persist[key];
                    storeString(key, value);
                } else if (isValidKey(key)) {
                    const value = persist[key];
                    storeString(key, value !== undefined ? JSON.stringify(value) : undefined);
                }
            }
        }),
        ignoreElements()
    );

const onTokenChanged: Epic = (action$, state$) =>
    action$.pipe(
        filter(isOfActions([actionCreators.onLogout, actionCreators.onWrongToken])),
        mergeMap(action => {
            if (action.type === actionCreators.onLogout.type) {
                localStorage.clear();
            } else {
                storeString("token", undefined);
            }
            return of();
        })
    );

// const onLogout: Epic = action$ =>
//     action$.pipe(
//         filter(isOfAction(actionCreators.onLogout)),
//         tap(() => {
//             window.localStorage.clear();
//         }),
//         ignoreElements()
//     );

type RoomViewLS = {
    roomId: string;
    viewId?: string;
    type: RoomType;
    searchQuery?: string;
    historyAroundTs?: number;
    historyHighlighedMessage?: string;
};

type RoomViewActiveLS =
    | {
          // old format
          roomId: string;
          viewId: undefined;
          type: RoomType;
      }
    | {
          // new format
          roomId: undefined;
          viewId: string;
          type: undefined;
      };

const restoreState: Epic = (action$, state$, { App }) => {
    const openRoomsLSOld = new PersistObject("opened_rooms", [] as string[]);
    const activeRoomsLSOld = new PersistObject("active_rooms", [] as string[]);

    const openRoomsOld: RoomViewLS[] = openRoomsLSOld.value.map(x => ({
        roomId: x,
        viewId: uuid(),
        type: RoomType.REAL_TIME,
    }));

    const activeRoomsOld: RoomViewActiveLS[] = activeRoomsLSOld.value.map(x => ({
        roomId: x,
        viewId: undefined,
        type: RoomType.REAL_TIME,
    }));

    const openRoomsLS = new PersistObject("openedRooms", openRoomsOld);
    const activeRoomsLS = new PersistObject("activeRooms", activeRoomsOld);

    const openRooms: I.List<RoomView> = listTakeLast(
        I.List(openRoomsLS.value),
        MAX_OPEN_ROOMS_NUM
    ).map(x => RoomView({ ...x, viewId: x.viewId || uuid() }));
    const findRoom = (room: RoomViewActiveLS) =>
        openRooms.find(x => x.viewId === room.viewId) ||
        openRooms.find(x => x.roomId === room.roomId && x.type === room.type);

    // make sure that we don't have rooms that are not opened
    const activeRooms = I.List(
        activeRoomsLS.value.map(room => findRoom(room)).filter(notUndefined)
    );

    openRoomsLSOld.clean();
    activeRoomsLSOld.clean();
    openRoomsLS.value = openRooms.toArray().map(
        ({
            roomId,
            viewId,
            type,
            searchQuery,
            historyAroundTs,
            historyHighlighedMessage,
        }): RoomViewLS => ({
            roomId,
            viewId,
            type,
            searchQuery,
            historyAroundTs,
            historyHighlighedMessage,
        })
    );
    activeRoomsLS.value = activeRooms
        .toArray()
        .map(({ viewId }) => ({ viewId, roomId: undefined, type: undefined }));

    return mergeStatic(
        action$.pipe(
            filter(isOfAction(actionCreators.appIsReady)),
            map(() =>
                actionCreators.restoreRooms({
                    activeRooms,
                    roomViews: unflattenRoomViews(openRooms),
                })
            )
        ),
        action$.pipe(
            filter(changesActiveRooms),
            debounceTime(100),
            tap(
                () =>
                    (activeRoomsLS.value = state$.value.rooms.activeViewIds
                        .toArray()
                        .map(x => ({ viewId: x, roomId: undefined, type: undefined })))
            ),
            ignoreElements()
        ),
        action$.pipe(
            filter(changesOpenedRooms),
            debounceTime(100),
            tap(
                () =>
                    (openRoomsLS.value = getFlattenRoomViews(state$.value)
                        .toArray()
                        .map(
                            ({
                                roomId,
                                viewId,
                                type,
                                searchQuery,
                                historyAroundTs,
                                historyHighlighedMessage,
                            }): RoomViewLS => ({
                                roomId,
                                viewId,
                                type,
                                searchQuery,
                                historyAroundTs,
                                historyHighlighedMessage,
                            })
                        ))
            ),
            ignoreElements()
        )
    );
};

export const persistEpic = combineEpics(
    onShowAppDevTools,
    onStoreObject,
    restoreState,
    onTokenChanged
);
